// QuotationFormat.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import Export from "../../../../formHelper/export";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import Table from "../../../../formHelper/list";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Success from "../../../../formHelper/success";
import Delete from "../../../../formHelper/delete";
import exportApiCall from "../../../../apiHelper/export";
import FilterDropdown from "../../../../formHelper/filterDropDown";
import Loader from "../../../../formHelper/Loader";
import dayjs from "dayjs";
import type { RootState, AppDispatch } from "../../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
// ---------- Types ----------
interface QuotationFormat {
  id: number;
  email: string;
  mobile: number | string;
  billing_address: string;
  branch_address: string;
  notes: string;
  branch_id: number | null;
  created_at: string;
  branch?: { name?: string };
  [k: string]: any;
}

interface Branch {
  id: number;
  name: string;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

// ---------- Component ----------
const QuotationFormat: React.FC = () => {
  const backendRoute = all_routes.backend;

  // data
  const [list, setList] = useState<QuotationFormat[]>([]);
  const [branchList, setBranchList] = useState<Branch[]>([]);

  // form
  const [formData, setFormData] = useState<any>({
    email: "",
    mobile: "",
    billing_address: "",
    branch_address: "",
    notes: "",
    branch_id: null,
  });
  const [formError, setFormError] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // ui
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // controls / filters
  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [branchFilter, setBranchFilter] = useState<number[]>([]);

  // pagination / status
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() =>
    typeof window !== "undefined" && window.innerWidth <= 768 ? 5 : 10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // refs
  const mountedRef = useRef(true);
  const paramsRef = useRef({
    page: currentPage,
    perPage,
    query,
    startDate,
    endDate,
    branchFilter,
  });
  const searchDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    paramsRef.current = {
      page: currentPage,
      perPage,
      query,
      startDate,
      endDate,
      branchFilter,
    };
  }, [currentPage, perPage, query, startDate, endDate, branchFilter]);

  useEffect(() => {
    mountedRef.current = true;
    // initial loads
    fetchList();
    getBranch();

    const onResize = () => {
      if (window.innerWidth <= 768) setPerPage(5);
      else if (perPage < 10) setPerPage(10);
    };
    window.addEventListener("resize", onResize);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("resize", onResize);
      if (searchDebounceRef.current)
        window.clearTimeout(searchDebounceRef.current);
    };
  }, [branchFilter]);

  // debounce search -> fetch
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = window.setTimeout(() => {
      setCurrentPage(1);
      fetchList({ page: 1, perPage: paramsRef.current.perPage });
    }, 450);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [query]);

  // Inject small helper CSS to match Source layout spacing
  useEffect(() => {
    const styleId = "quotationformat-responsive-styles";
    if (document.getElementById(styleId)) return;

    const css = `
      /* align small layout bits to mirror Source.tsx */
      .page-header { margin-bottom: 0.5rem; }
      .count-title, .count-badge { background: rgba(0,0,0,0.04); padding:4px 8px; border-radius:8px; font-weight:600; }
      .icon-form .form-icon { margin-right:0.6rem; display:flex; align-items:center; position:absolute; left:0.85rem; top:50%; transform:translateY(-50%); pointer-events:none; }
      .card-control .form-control.bookingrange { padding-left: 2.6rem; }
      .filter-dropdown-menu .filter-set-view { min-width:220px; }
      .offcanvas.show { visibility: visible !important; }
    `;
    const style = document.createElement("style");
    style.id = styleId;
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // ---------- API helpers ----------
  const safeSet = useCallback(<T,>(setter: (v: T) => void, val: T) => {
    if (mountedRef.current) setter(val);
  }, []);

  const fetchList = useCallback(
    async (params?: { page?: number; perPage?: number }) => {
      const payloadPage = params?.page ?? paramsRef.current.page ?? 1;
      const payloadPer =
        params?.perPage ?? paramsRef.current.perPage ?? perPage;

      setLoading(true);
      try {
        const result = await apiRequestCall(
          "post",
          backendRoute.getQuotationFormat,
          {
            data: {
              page: payloadPage,
              per_page: payloadPer,
              start_date: paramsRef.current.startDate,
              end_date: paramsRef.current.endDate,
              search: (paramsRef.current.query || "").trim(),
              branch_list: paramsRef.current.branchFilter,
            },
          }
        );

        const res = result?.data?.data;
        if (res) {
          safeSet(setList, res.data || []);
          safeSet(setCurrentPage, res.current_page ?? payloadPage);
          safeSet(setPerPage, res.per_page ?? payloadPer);
          safeSet(setTotalItems, res.total ?? 0);
        } else {
          safeSet(setList, []);
        }
      } catch (err) {
        console.error("Failed to fetch quotation formats:", err);
        safeSet(setList, []);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [backendRoute.getQuotationFormat, perPage, safeSet]
  );

  const getBranch = useCallback(async () => {
    try {
      const result = await apiRequestCall("post", backendRoute.getBranchDD);
      const raw = result?.data?.data;
      if (raw && typeof raw === "object") {
        const arr = Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }));
        safeSet(setBranchList, arr);
      }
    } catch (e) {
      console.error("getBranch failed", e);
    }
  }, [backendRoute.getBranchDD, safeSet]);

  // ---------- export helpers ----------
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  // Poll API for local download
  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportFile = useCallback(async () => {
    try {
      safeSet(setExporting, true);
      safeSet(setDownloading, false);

      const result = await apiRequestCall(
        "post",
        backendRoute.getQuotationFormat,
        {
          data: {
            search: (query || "").trim(),
            start_date: startDate,
            end_date: endDate,
            branch_list: branchFilter,
            download: true,
          },
        }
      );

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        safeSet(setExporting, false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename);
    } catch (error) {
      console.error("Export request failed:", error);
      safeSet(setExporting, false);
      safeSet(setDownloading, false);
      alert("Export failed. Please try again.");
    }
  }, [
    backendRoute.getQuotationFormat,
    branchFilter,
    pollForFile,
    query,
    startDate,
    endDate,
    triggerDownload,
    safeSet,
  ]);

  // ---------- form / actions ----------
  const inputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormError((prev) => {
        const updated = { ...prev };
        delete (updated as any)[name];
        return updated;
      });
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData({
      email: "",
      mobile: "",
      billing_address: "",
      branch_address: "",
      notes: "",
      branch_id: null,
    });
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        quotation_format_id: selectedId,
        update_status: !!selectedId,
      };

      const result = await apiRequestCall(
        "post",
        backendRoute.addUpdateQuotationFormat,
        { data: payload }
      );

      if (result?.data?.code === 221) {
        setFormError(result?.data?.errors || {});
        return;
      }

      if (result?.data?.code === 200) {
        resetForm();
        setShowSuccess(true);
        setShowMessage(result?.data?.message || "");
        // refresh current page
        fetchList({ page: currentPage, perPage });
      }
    } catch (err) {
      console.error("Submit failed:", err);
      alert("Request failed. Please try again.");
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [
    backendRoute.addUpdateQuotationFormat,
    currentPage,
    formData,
    perPage,
    resetForm,
    selectedId,
    submitting,
  ]);

  const updateRecord = useCallback(
    (id: number) => {
      setFormError({});
      const selected = list.find((x) => x.id === id);
      if (!selected) return;
      setFormData({ ...selected });
      setSelectedId(selected.id);
      setShowForm(true);
    },
    [list]
  );

  const deleteRecord = useCallback((id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await apiRequestCall(
        "post",
        backendRoute.deleteQuotationFormat,
        {
          data: { id: deleteId },
        }
      );
      if (result?.data?.code === 200) {
        fetchList({ page: currentPage, perPage });
        setShowMessage(result?.data?.message || "");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed. Please try again.");
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      setDeleting(false);
    }
  }, [
    backendRoute.deleteQuotationFormat,
    currentPage,
    deleteId,
    perPage,
    deleting,
  ]);

  // ---------- memoized UI pieces ----------
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  const exportOptions = useMemo(
    () => [
      {
        label: downloading
          ? "Downloading..."
          : exporting
          ? "Generating..."
          : "Export",
        iconClass: downloading
          ? "ti ti-download"
          : exporting
          ? "ti ti-loader"
          : "ti ti-file-type-xls",
        colorClass: exporting || downloading ? "text-muted" : "text-primary",
        onClick: exporting || downloading ? undefined : exportFile,
      },
    ],
    [downloading, exporting, exportFile]
  );

  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "Sr. No",
        render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
      },
      { key: "email", label: "Email" },
      { key: "mobile", label: "Mobile" },
      { key: "billing_address", label: "Billing Address" },
      { key: "branch_address", label: "Branch Address" },
      { key: "branch.name", label: "Branch" },
      { key: "notes", label: "Notes" },
      {
        key: "created_at",
        label: "Date",
      },
      ...(permissionMap?.quotation_format?.edit_quotation_format ||
      permissionMap?.quotation_format?.delete_quotation_format
        ? [
            {
              key: "action",
              label: "Action",
              render: (row: QuotationFormat) => (
                <div className="hstack gap-2 fs-15">
                  {permissionMap?.quotation_format?.edit_quotation_format && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-info"
                      onClick={() => updateRecord(row.id)}
                      disabled={submitting}
                      title="Edit"
                    >
                      <i className="feather-edit" />
                    </button>
                  )}

                  {permissionMap?.quotation_format?.delete_quotation_format && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-danger"
                      onClick={() => deleteRecord(row.id)}
                      disabled={submitting}
                      title="Delete"
                    >
                      <i className="feather-trash" />
                    </button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [currentPage, perPage, submitting, deleting, updateRecord]
  );

  // auth
  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  // ---------- render ----------
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header: Title | (mobile-only Export) | New */}
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  {isMobile ? "Format" : "Quotation Format"}{" "}
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  {permissionMap?.["quotation_format"]?.[
                    "export_quotation_format"
                  ] && <Export options={exportOptions} />}

                  {permissionMap?.["quotation_format"]?.[
                    "add_quotation_format"
                  ] && (
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      disabled={submitting}
                      aria-label="Add new source"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls card (search / date / export/ filter) — matches Source.tsx layout */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Search Format"
                    value={query}
                    onChange={(v) => setQuery(v)}
                  />
                </div>
              </div>

              <div className="col-12 col-md-3">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                      setCurrentPage(1);
                      fetchList({ page: 1, perPage });
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                      setCurrentPage(1);
                      fetchList({ page: 1, perPage });
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? <Loader /> : <Table data={list} columns={columns} />}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p) => {
              setCurrentPage(p);
              fetchList({ page: p, perPage });
            }}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setCurrentPage(1);
              fetchList({ page: 1, perPage: pp });
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>

        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          style={{ visibility: showForm ? "visible" : "hidden" }}
          tabIndex={-1}
          aria-modal={showForm}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              {selectedId ? "Update Format" : "Add Format"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => !submitting && setShowForm(false)}
              disabled={submitting}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!submitting) handleSubmit();
              }}
            >
              <div className="mb-3">
                <label className="col-form-label">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.email && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.email}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Mobile <span className="text-danger">*</span>
                </label>
                <input
                  name="mobile"
                  type="text"
                  className="form-control"
                  value={String(formData.mobile || "")}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.mobile && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.mobile}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Select Branch <span className="text-danger">*</span>
                </label>
                <Select
                  name="branch_id"
                  classNamePrefix="react-select"
                  className="tagging"
                  options={branchList.map((b) => ({
                    value: b.id,
                    label: b.name,
                  }))}
                  value={
                    branchList
                      .map((b) => ({ value: b.id, label: b.name }))
                      .find((opt) => opt.value === formData.branch_id) || null
                  }
                  onChange={(selected: any) => {
                    setFormError((prev) => {
                      const updated = { ...prev };
                      delete updated.branch_id;
                      return updated;
                    });
                    setFormData((prev: any) => ({
                      ...prev,
                      branch_id: selected?.value || null,
                    }));
                  }}
                  isClearable
                  isDisabled={submitting}
                />
                {formError.branch_id && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.branch_id}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Billing Address <span className="text-danger">*</span>
                </label>
                <textarea
                  name="billing_address"
                  className="form-control"
                  style={{ height: "120px" }}
                  value={formData.billing_address}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.billing_address && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.billing_address}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Branch Address <span className="text-danger">*</span>
                </label>
                <textarea
                  name="branch_address"
                  className="form-control"
                  style={{ height: "120px" }}
                  value={formData.branch_address}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.branch_address && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.branch_address}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Notes <span className="text-danger">*</span>
                </label>
                <textarea
                  name="notes"
                  className="form-control"
                  style={{ height: "100px" }}
                  value={formData.notes}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.notes && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.notes}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {selectedId ? "Updating..." : "Creating..."}
                    </>
                  ) : selectedId ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <Success
          id="formatSuccessModal"
          title="Quotation Format"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />

        <Delete
          show={showDeleteModal}
          title="Delete Format?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default QuotationFormat;
